import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Icon, Popup } from 'semantic-ui-react';
import { applyTheme, getAllThemes, getCurrentTheme, Swatch } from '../../../themes';
import { DOC_LINKS, FEEDBACK_ITEMS, HELP_FALLBACK, openForumIntent } from './HelpMenu';
import { languageOptions } from './LanguageSelector';
import { trackAIToggle } from './AIToggle';

const ITEM_SELECTOR = '[role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"]';

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

// The ☰ menu of the desktop top bar: holds whichever right-section controls did not fit
// in the row (see RightSection's fold order). Each folded control keeps its behavior.
const OverflowMenu = ({
  folded,
  panelOpen,
  zoomOptions,
  zoomValue,
  onZoomChange,
  notesVisible,
  aiVisible,
  showAI,
  onNotesToggle,
  onAIToggle,
  help_l,
  is_user_signed_in,
  setTriedToSave,
  currentLanguage
}) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null); // 'language' | 'help' | null
  const [currentTheme, setCurrentTheme] = useState(getCurrentTheme());
  const menuRef = useRef(null);
  const words = window.l || {};
  const helpWords = { ...HELP_FALLBACK, ...(help_l || {}) };
  const isFolded = (key) => folded.indexOf(key) !== -1;

  useEffect(() => {
    const handleThemeChange = (e) => setCurrentTheme(e.detail.theme);
    window.addEventListener('themechange', handleThemeChange);
    return () => window.removeEventListener('themechange', handleThemeChange);
  }, []);

  // Move focus into the portal-rendered menu when it opens
  useEffect(() => {
    if (!open) {
      setExpanded(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      const first = menuRef.current && menuRef.current.querySelector(ITEM_SELECTOR);
      if (first) first.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [open]);

  // Deferred: the popup unmounts its content as it closes, which would otherwise drop focus
  const focusTrigger = () => {
    setTimeout(() => {
      const trigger = document.getElementById('lab_overflow_button');
      if (trigger) trigger.focus();
    }, 0);
  };

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback((e) => {
    setOpen(false);
    // Popup closes on Escape by itself too; Escape always hands focus back to the trigger
    if (e && e.key === 'Escape') focusTrigger();
  }, []);

  // Non-link items: close, act, and hand focus back to the trigger
  const handleChoose = (action) => (e) => {
    setOpen(false);
    action(e);
    focusTrigger();
  };

  const handleKeyDown = (e) => {
    // Tab would leave the portal-rendered menu for the end of the page: close it instead
    if (e.key === 'Escape' || e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      focusTrigger();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    const items = Array.from(menuRef.current ? menuRef.current.querySelectorAll(ITEM_SELECTOR) : []);
    if (items.length === 0) return;
    e.preventDefault();
    const index = items.indexOf(document.activeElement);
    let next = 0;
    if (e.key === 'ArrowDown') next = index < 0 ? 0 : (index + 1) % items.length;
    if (e.key === 'ArrowUp') next = index <= 0 ? items.length - 1 : index - 1;
    if (e.key === 'End') next = items.length - 1;
    items[next].focus();
  };

  const handleToggleExpanded = (key) => () => setExpanded((prev) => (prev === key ? null : key));

  const handleThemeSelect = (themeId) => {
    if (themeId !== currentTheme) applyTheme(themeId);
  };

  const handleAIToggle = (e) => {
    trackAIToggle(aiVisible);
    onAIToggle(e);
  };

  const themes = getAllThemes();
  const notesOn = notesVisible && !aiVisible;
  const language = languageOptions.find((option) => option.value === currentLanguage);
  const moreLabel = words.more || 'More';

  const menu = (
    <div ref={menuRef} className="rs-overflow-menu" role="menu" aria-label={moreLabel} onKeyDown={handleKeyDown}>
      {isFolded('create') && (
        <>
          <a
            className="rs-om-item create"
            role="menuitem"
            tabIndex={-1}
            href="/users/sign_up"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClose}
          >
            {capitalize(words.create_an_account || 'create an account')}
          </a>
          <div className="rs-om-divider" role="separator" />
        </>
      )}

      {isFolded('theme') && (
        <div role="group" aria-label={words.theme || 'Theme'}>
          <div className="rs-om-label">
            <Swatch themeId={currentTheme} size={16} idSuffix="-overflow" />
            {words.theme || 'Theme'}
          </div>
          <div className="rs-om-sub">
            {themes.map((theme) => (
              <button
                key={theme.id}
                type="button"
                className={`rs-om-swatch ${currentTheme === theme.id ? 'on' : ''}`}
                role="menuitemradio"
                aria-checked={currentTheme === theme.id}
                aria-label={theme.name}
                title={theme.name}
                tabIndex={-1}
                onClick={handleChoose(() => handleThemeSelect(theme.id))}
              >
                <Swatch themeId={theme.id} size={22} idSuffix="-overflow-item" />
              </button>
            ))}
          </div>
        </div>
      )}

      {isFolded('zoom') && (
        <div role="group" aria-label={words.adjust_board_zoom || 'Adjust board zoom'}>
          <div className="rs-om-label">
            <Icon name="search" style={{ margin: 0 }} />
            {words.adjust_board_zoom || 'Adjust board zoom'}
          </div>
          <div className="rs-om-sub">
            {zoomOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`rs-om-chip ${zoomValue === option.value ? 'on' : ''}`}
                role="menuitemradio"
                aria-checked={zoomValue === option.value}
                tabIndex={-1}
                onClick={handleChoose((e) => onZoomChange(e, { value: option.value }))}
              >
                {option.text}
              </button>
            ))}
          </div>
        </div>
      )}

      {isFolded('notes') && (
        <>
          <button
            type="button"
            className="rs-om-item"
            role="menuitemcheckbox"
            aria-checked={notesOn}
            tabIndex={-1}
            onClick={handleChoose(onNotesToggle)}
          >
            <Icon name={notesOn ? 'file text' : 'file text outline'} style={{ margin: 0 }} />
            {words.notes || 'Notes'}
            <span className={`rs-om-toggle ${notesOn ? 'on' : ''}`} aria-hidden="true" />
          </button>
          {showAI && (
            <button
              type="button"
              className="rs-om-item"
              role="menuitemcheckbox"
              aria-checked={!!aiVisible}
              tabIndex={-1}
              onClick={handleChoose(handleAIToggle)}
            >
              <Icon name="wechat" style={{ margin: 0 }} />
              {words.ai_assistant || 'AI assistant'}
              <span className={`rs-om-toggle ${aiVisible ? 'on' : ''}`} aria-hidden="true" />
            </button>
          )}
        </>
      )}

      {isFolded('help') && (
        <>
          <button
            type="button"
            className="rs-om-item"
            role="menuitem"
            aria-expanded={expanded === 'help'}
            tabIndex={-1}
            onClick={handleToggleExpanded('help')}
          >
            <Icon name="question circle outline" style={{ margin: 0 }} />
            {helpWords.help}
            <Icon className="rs-om-trailing" name={expanded === 'help' ? 'angle down' : 'angle right'} style={{ margin: '0 0 0 auto' }} />
          </button>
          {expanded === 'help' && (
            <div className="rs-om-nested" role="group" aria-label={helpWords.help}>
              {DOC_LINKS.map((item) => (
                <a
                  key={item.key}
                  className="rs-om-item"
                  role="menuitem"
                  tabIndex={-1}
                  href={item.href}
                  target="_blank"
                  rel="noopener"
                  onClick={handleClose}
                >
                  <Icon name={item.icon} style={{ margin: 0 }} />
                  {helpWords[item.key]}
                </a>
              ))}
              <div className="rs-om-divider" role="separator" />
              {FEEDBACK_ITEMS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="rs-om-item"
                  role="menuitem"
                  tabIndex={-1}
                  onClick={handleChoose(() => openForumIntent(item.category, is_user_signed_in, setTriedToSave))}
                >
                  <Icon name={item.icon} style={{ margin: 0 }} />
                  {helpWords[item.key]}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {isFolded('language') && (
        <>
          <button
            type="button"
            className="rs-om-item"
            role="menuitem"
            aria-expanded={expanded === 'language'}
            tabIndex={-1}
            onClick={handleToggleExpanded('language')}
          >
            <Icon name="world" style={{ margin: 0 }} />
            {words.language || 'Language'}
            <span className="rs-om-trailing">
              {language ? language.text : ''}
              <Icon name={expanded === 'language' ? 'angle down' : 'angle right'} style={{ margin: '0 0 0 4px' }} />
            </span>
          </button>
          {expanded === 'language' && (
            <div className="rs-om-nested" role="group" aria-label={words.language || 'Language'}>
              {languageOptions.map((option) => (
                <a
                  key={option.key}
                  className={`rs-om-item ${option.value === currentLanguage ? 'current' : ''}`}
                  role="menuitemradio"
                  aria-checked={option.value === currentLanguage}
                  tabIndex={-1}
                  href={option.link}
                  onClick={handleClose}
                >
                  {option.text}
                </a>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <>
      <style>{`
        .rs-overflow-trigger { position: relative !important; }
        .rs-overflow-dot {
          position: absolute;
          top: 5px;
          right: 4px;
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--chart-2);
          border: 1px solid var(--popover);
          pointer-events: none;
        }
        .ui.popup.rs-overflow-popup {
          padding: 0 !important;
          background: var(--popover) !important;
          color: var(--popover-foreground) !important;
          border: 1px solid var(--border) !important;
          border-radius: var(--radius) !important;
          box-shadow: var(--shadow-lg) !important;
        }
        .rs-overflow-menu {
          min-width: 220px;
          max-height: calc(100vh - 90px);
          overflow-y: auto;
          padding: 4px 0;
          font-size: 12px;
          color: var(--popover-foreground);
        }
        .rs-overflow-menu .rs-om-item {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 8px 12px;
          background: transparent;
          border: 0;
          border-radius: 0;
          color: var(--popover-foreground);
          font-family: inherit;
          font-size: 12px;
          line-height: 1.2;
          text-align: left;
          text-decoration: none;
          cursor: pointer;
          white-space: nowrap;
        }
        .rs-overflow-menu .rs-om-item:hover,
        .rs-overflow-menu .rs-om-item:focus-visible,
        .rs-overflow-menu .rs-om-item:focus {
          background: var(--accent);
          color: var(--popover-foreground);
          outline: none;
        }
        .rs-overflow-menu .rs-om-item.create {
          font-weight: 600;
          color: var(--chart-4);
        }
        .rs-overflow-menu .rs-om-item.current { font-weight: 600; }
        .rs-overflow-menu .rs-om-nested .rs-om-item { padding-left: 32px; }
        .rs-overflow-menu .rs-om-divider {
          border-top: 1px solid var(--border);
          margin: 4px 0;
        }
        .rs-overflow-menu .rs-om-label {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px 4px;
        }
        .rs-overflow-menu .rs-om-sub {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          padding: 2px 12px 8px 32px;
        }
        .rs-overflow-menu .rs-om-chip {
          padding: 3px 7px;
          border: 1px solid var(--border);
          border-radius: var(--radius);
          background: transparent;
          color: var(--popover-foreground);
          font-family: inherit;
          font-size: 12px;
          cursor: pointer;
        }
        .rs-overflow-menu .rs-om-chip:hover,
        .rs-overflow-menu .rs-om-chip:focus { background: var(--accent); outline: none; }
        .rs-overflow-menu .rs-om-chip.on {
          background: var(--chart-2);
          color: var(--primary-foreground);
          border-color: transparent;
        }
        .rs-overflow-menu .rs-om-swatch {
          width: 26px;
          height: 26px;
          padding: 0;
          border: 0;
          border-radius: 50%;
          background: transparent;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }
        .rs-overflow-menu .rs-om-swatch.on { box-shadow: 0 0 0 2px var(--popover), 0 0 0 4px var(--primary); }
        .rs-overflow-menu .rs-om-swatch:focus { outline: none; box-shadow: 0 0 0 2px var(--popover), 0 0 0 4px var(--ring); }
        .rs-overflow-menu .rs-om-trailing {
          margin-left: auto;
          display: flex;
          align-items: center;
          color: var(--muted-foreground);
        }
        .rs-overflow-menu .rs-om-toggle {
          margin-left: auto;
          width: 28px;
          height: 16px;
          border-radius: 9px;
          background: var(--border);
          position: relative;
          flex-shrink: 0;
        }
        .rs-overflow-menu .rs-om-toggle:after {
          content: "";
          position: absolute;
          left: 2px;
          top: 2px;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: var(--popover);
          transition: left 0.15s ease;
        }
        .rs-overflow-menu .rs-om-toggle.on { background: var(--chart-2); }
        .rs-overflow-menu .rs-om-toggle.on:after { left: 14px; }
      `}</style>
      <Popup
        on="click"
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        position="bottom right"
        basic
        className="rs-overflow-popup"
        trigger={
          <Button
            id="lab_overflow_button"
            className="right-section-btn rs-overflow-trigger"
            active={open}
            size="mini"
            style={{ marginLeft: '8px', padding: '0.7em', justifyContent: 'center' }}
            aria-label={moreLabel}
            aria-haspopup="menu"
            aria-expanded={open}
            title={moreLabel}
          >
            <Icon name="bars" size="large" style={{ margin: 0 }} />
            {panelOpen && <span className="rs-overflow-dot" aria-hidden="true" />}
          </Button>
        }
      >
        {menu}
      </Popup>
    </>
  );
};

export default OverflowMenu;
